import PHONE_COUNTRY_CODES from './phone-country-codes.js';
import { createCountryListbox, focusVerificationError } from './phone-country-listbox.js';
import {
    composeE164 as composePhoneNumber,
    splitInternationalNumber,
} from './phone-number-composer.js';
import {
    apiFailure,
    confirmWithCachedToken,
    createCooldownController,
    FIREBASE_MESSAGE_KEYS,
    verifierFailureReport,
} from './phone-verification-flow.js';
import {
    safeStorageGet,
    safeStorageRemove,
    safeStorageSet,
    SESSION_STORAGE,
} from './safe-storage.js';

(function () {
    'use strict';

    const ACCOUNT_URL = '/app/account';
    const FLAG_BASE = '/images/circle-flags/flags/';
    const DEFAULT_COUNTRY_ISO = 'us';

    // Where several countries share one dial code, name the one a typed prefix
    // should resolve to. Without this the first alphabetical entry wins, which
    // shows Kazakhstan for +7 and Guadeloupe for +590.
    const PRIMARY_DIAL_ISO = {
        1: 'us',
        7: 'ru',
        39: 'it',
        44: 'gb',
        47: 'no',
        61: 'au',
        212: 'ma',
        262: 're',
        290: 'sh',
        358: 'fi',
        590: 'gp',
        599: 'cw',
    };

    // The dial codes ship with the bundle, the labels come from the server in
    // the viewer's language, so both the display order and the search haystack
    // are rebuilt whenever the labels are applied.
    let countries = PHONE_COUNTRY_CODES.slice();
    let countriesByDialLength = [];

    function reindexCountries() {
        countries.sort((a, b) => a.name.localeCompare(b.name));
        // Longest dial code first so real multi-digit codes win over a shorter
        // prefix. Countries in the +1 numbering plan stay a picker choice
        // because their area codes are part of the national number.
        countriesByDialLength = countries.slice().sort((a, b) => b.dial.length - a.dial.length);
    }

    /** @param {Object<string, string>} names ISO-3166-1 alpha-2 (uppercase) to label. */
    function applyCountryNames(names) {
        countries = PHONE_COUNTRY_CODES.map((country) => {
            const localized = names[country.iso.toUpperCase()];

            return localized ? { ...country, name: localized } : country;
        });

        reindexCountries();
    }

    reindexCountries();

    function countryByIso(iso) {
        return countries.find((c) => c.iso === iso) || null;
    }

    function countryForDial(dialDigits) {
        if (!dialDigits) return null;

        const match = countriesByDialLength.find((c) => dialDigits.startsWith(c.dial));
        if (!match) return null;

        const primaryIso = PRIMARY_DIAL_ISO[match.dial];

        return (primaryIso && countryByIso(primaryIso)) || match;
    }

    function digitsOnly(value) {
        return String(value || '').replace(/\D/g, '');
    }
    const PHONE_PATTERN = /^\+[1-9]\d{6,14}$/;
    const CODE_PATTERN = /^\d{6}$/;
    const RESEND_COOLDOWN_SECONDS = 60;
    const RATE_LIMITED = 'rate_limited';
    const RESEND_COOLDOWN = 'resend_cooldown';
    const BLOCKED = 'blocked';
    // The server has no App Check site key, so the check cannot run. Kept apart
    // from BLOCKED: the cause is ours, and the user has nothing to turn off.
    const UNCONFIGURED = 'unconfigured';
    // The gate is no longer pending: waived, already verified, or gone.
    const INVALID_STATE = 'invalid_state';
    const HTTP_CONFLICT = 409;
    const COOLDOWN_STORAGE_KEY = 'phone-verification-cooldown-until';
    // api.js serves a small stub that defines render() before the full library
    // has arrived, so readiness is taken from the documented onload callback
    // rather than from the presence of render().
    const RECAPTCHA_READY_CALLBACK = '__interpalsRecaptchaReady';
    const RECAPTCHA_MIRROR_SRC = 'https://www.recaptcha.net/recaptcha/api.js'
        + '?render=explicit&onload=' + RECAPTCHA_READY_CALLBACK;
    const RECAPTCHA_LOAD_TIMEOUT_MS = 8000;
    const CODE_LENGTH = 6;

    // Firebase's own throttle and quota map onto the two informational boxes
    // rather than the red error box.
    const FIREBASE_RATE_LIMITED = 'auth/too-many-requests';
    const FIREBASE_QUOTA_EXCEEDED = 'auth/quota-exceeded';

    // Failures are told apart by `code` rather than by message text, so the
    // handler can pick the message the user is shown.
    function codedError(code, retryAfter) {
        const error = new Error(code);
        error.code = code;
        if (retryAfter) {
            error.retryAfter = retryAfter;
        }

        return error;
    }

    function ready(fn) {
        if (document.readyState !== 'loading') {
            fn();
            return;
        }

        document.addEventListener('DOMContentLoaded', fn);
    }

    function parseJsonAttribute(root, attribute) {
        try {
            return JSON.parse(root.getAttribute(attribute) || '{}');
        } catch (e) {
            return {};
        }
    }

    function createBody(values) {
        const body = new FormData();
        Object.entries(values).forEach(([key, value]) => {
            body.append(key, String(value));
        });

        return body;
    }

    async function post(endpoint, values) {
        if (!window.ApiClient) {
            throw new Error('api_client_missing');
        }

        return window.ApiClient.request(endpoint, {
            method: 'POST',
            body: createBody(values),
        });
    }

    async function responseJson(response) {
        try {
            return await response.json();
        } catch (e) {
            return {};
        }
    }

    function setHidden(element, hidden) {
        if (element) {
            element.classList.toggle('hidden', hidden);
        }
    }

    function setBusy(button, spinnerId, busy) {
        if (button) {
            button.disabled = busy;
        }

        setHidden(document.getElementById(spinnerId), !busy);
    }

    ready(function () {
        const root = document.getElementById('verify-phone-root');
        if (!root) return;

        window.LucideIcons?.initialize(root);

        const config = parseJsonAttribute(root, 'data-firebase-config');
        const firebaseAppCheckSiteKey = root.getAttribute('data-firebase-app-check-site-key') || '';
        const messages = parseJsonAttribute(root, 'data-messages');
        applyCountryNames(parseJsonAttribute(root, 'data-country-names'));
        const redirectUrl = root.getAttribute('data-redirect') || ACCOUNT_URL;
        const phoneForm = document.getElementById('verify-phone-phone-form');
        const codeForm = document.getElementById('verify-phone-code-form');
        const phoneInput = document.getElementById('verify-phone-number');
        const codeInput = document.getElementById('verify-phone-code');
        const codeField = document.getElementById('verify-phone-code-field');
        const codeSlots = codeField
            ? Array.prototype.slice.call(codeField.querySelectorAll('[data-code-slot]'))
            : [];
        const sendButton = document.getElementById('verify-phone-send');
        const confirmButton = document.getElementById('verify-phone-confirm');
        const resendButton = document.getElementById('verify-phone-resend');
        const changeNumberButton = document.getElementById('verify-phone-change-number');
        const countryButton = document.getElementById('verify-phone-country-button');
        const countryPanel = document.getElementById('verify-phone-country-panel');
        const countryList = document.getElementById('verify-phone-country-list');
        const countrySearch = document.getElementById('verify-phone-country-search');
        const countryEmpty = document.getElementById('verify-phone-country-empty');
        const countryFlag = document.getElementById('verify-phone-country-flag');
        const countryDial = document.getElementById('verify-phone-country-dial');
        const countryWrapper = document.getElementById('verify-phone-country');
        const resendLabel = document.getElementById('verify-phone-resend-label');
        const resendDefaultLabel = resendLabel ? resendLabel.textContent : '';
        const sendSpinnerId = 'verify-phone-send-spinner';
        const confirmSpinnerId = 'verify-phone-confirm-spinner';
        const resendSpinnerId = 'verify-phone-resend-spinner';
        const errorBox = document.getElementById('verify-phone-error');
        const errorText = document.getElementById('verify-phone-error-text');
        const rateLimitedBox = document.getElementById('verify-phone-rate-limited');
        const resendCooldownBox = document.getElementById('verify-phone-resend-cooldown');
        const unavailableBox = document.getElementById('verify-phone-unavailable');
        const successBox = document.getElementById('verify-phone-success');
        const providerWaivedBox = document.getElementById('verify-phone-provider-waived');
        const fallbackBox = document.getElementById('verify-phone-fallback');
        const fallbackButtons = fallbackBox
            ? Array.prototype.slice.call(fallbackBox.querySelectorAll('[data-channel]'))
            : [];
        const fallbackSpinnerId = 'verify-phone-fallback-spinner';

        if (!phoneForm || !codeForm || !phoneInput || !codeInput) return;

        const state = {
            appCheckInitialized: false,
            auth: null,
            confirmationResult: null,
            firebaseIdToken: '',
            firebaseUser: null,
            nonce: '',
            recaptchaVerifier: null,
            recaptchaWidgetId: null,
            sessionGeneration: 0,
            sending: false,
            confirming: false,
            country: null,
            // The number this session reserved. The fallback send has to name
            // the same one, and by then the phone form is hidden.
            phone: '',
            // Set once the fallback provider accepted a send, which is what
            // routes the confirm to the fallback endpoint.
            fallbackChannel: '',
        };

        const cooldown = createCooldownController({
            clearExpiry: () => safeStorageRemove(COOLDOWN_STORAGE_KEY, SESSION_STORAGE),
            clearInterval: window.clearInterval.bind(window),
            defaultLabel: resendDefaultLabel,
            defaultSeconds: RESEND_COOLDOWN_SECONDS,
            formatLabel: resendCooldownLabel,
            now: Date.now,
            onRestore: () => {
                hideMessages();
                setHidden(resendCooldownBox, false);
            },
            readExpiry: () => safeStorageGet(COOLDOWN_STORAGE_KEY, SESSION_STORAGE),
            resendButton,
            resendLabel,
            sendButton,
            setInterval: window.setInterval.bind(window),
            writeExpiry: (expiresAt) => safeStorageSet(
                COOLDOWN_STORAGE_KEY,
                String(expiresAt),
                SESSION_STORAGE,
            ),
        });
        cooldown.restore();

        const hasCountryPicker = Boolean(
            countryButton && countryPanel && countryList && countrySearch && countryEmpty
                && countryDial && countryFlag && countryWrapper
        );

        // The dial field is sized by its `size` attribute rather than a width
        // class, so each country code gets exactly the room it needs and the
        // national number always starts right after it.
        function resizeDialInput() {
            countryDial.size = Math.max(2, countryDial.value.length);
        }

        function applyCountry(country, syncDialInput) {
            if (!country) return;

            state.country = country;
            countryFlag.src = FLAG_BASE + (country.flag || country.iso) + '.svg';
            countryFlag.alt = country.name;

            if (syncDialInput !== false) {
                countryDial.value = '+' + country.dial;
                resizeDialInput();
            }
        }

        // The picker and the typed dial code are two views of one value, so a typed
        // prefix moves the flag without rewriting what is being typed.
        function syncCountryFromDialInput() {
            const country = countryForDial(digitsOnly(countryDial.value));
            if (country && (!state.country || country.iso !== state.country.iso)) {
                applyCountry(country, false);
            }
        }

        function countryOption(country, onSelect) {
            const item = document.createElement('li');
            const button = document.createElement('button');
            button.type = 'button';
            button.id = 'verify-phone-country-option-' + country.iso;
            button.setAttribute('role', 'option');
            button.setAttribute(
                'aria-selected',
                state.country && state.country.iso === country.iso ? 'true' : 'false',
            );
            button.tabIndex = -1;
            button.className = 'flex min-h-[44px] w-full touch-manipulation items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-sky-50 dark:text-gray-200 dark:hover:bg-sky-950/40';

            const flag = document.createElement('img');
            flag.src = FLAG_BASE + (country.flag || country.iso) + '.svg';
            flag.alt = '';
            flag.loading = 'lazy';
            flag.className = 'h-5 w-5 flex-shrink-0 rounded-full object-cover';

            const name = document.createElement('span');
            name.className = 'min-w-0 flex-1 truncate';
            name.textContent = country.name;

            const dial = document.createElement('span');
            dial.className = 'flex-shrink-0 text-gray-500 dark:text-gray-400';
            dial.textContent = '+' + country.dial;

            button.append(flag, name, dial);
            button.addEventListener('click', onSelect);

            item.appendChild(button);

            return item;
        }

        // Both halves are normalized by the shared composer. The edited dial is
        // authoritative; matching country metadata supplies national-prefix rules.
        function composeE164() {
            const dial = digitsOnly(countryDial && countryDial.value);
            const selectedCountry = state.country && state.country.dial === dial
                ? state.country
                : countryForDial(dial);

            return composePhoneNumber(
                selectedCountry,
                phoneInput.value,
                dial,
            );
        }

        // A number copied from a contact list carries its own country code, as
        // '+61...' or as the '0061...' international prefix. Left alone it gets
        // appended to whatever country is selected and composes a malformed
        // '+1 61...', so it is split across the two fields instead.
        //
        // Without a leading '+' the intent is ambiguous, and guessing on every
        // keystroke would yank the country out from under someone mid-type. So
        // the unmarked form is only reinterpreted on paste, and only when the
        // digits cannot be a national number for the selected country: NANP
        // (+1, our default) is exactly 10 digits, and E.164 caps the whole
        // number at 15.
        function absorbInternationalNumber(isPaste) {
            const parsed = splitInternationalNumber(
                phoneInput.value,
                countryDial.value,
                isPaste,
                countryForDial,
            );
            if (!parsed) return;

            applyCountry(parsed.country);
            phoneInput.value = parsed.nationalNumber;
        }

        function setResendBusy(busy) {
            // While a resend cooldown counts down, the button stays disabled, so a
            // finishing send (busy=false) must not re-enable it. Only the cooldown
            // ending re-enables it.
            cooldown.setResendBusy(busy);

            const resendSpinner = document.getElementById(resendSpinnerId);
            if (resendSpinner) {
                resendSpinner.classList.toggle('animate-spin', busy);
            }
        }

        function resendCooldownLabel(seconds) {
            if (window.trans) {
                return window.trans('auth', 'phone_verify_resend_in', { seconds: seconds });
            }

            return 'Resend in ' + seconds + 's';
        }

        function startResendCooldown(seconds) {
            cooldown.start(seconds);
        }

        function changeNumber() {
            if (state.sending || state.confirming) return;

            // Return to the phone step. Drop any pending confirmation and nonce so a
            // stale Firebase session can't confirm against the previous number.
            state.sessionGeneration += 1;
            state.confirmationResult = null;
            state.firebaseIdToken = '';
            state.firebaseUser = null;
            state.nonce = '';
            state.fallbackChannel = '';
            setHidden(fallbackBox, true);
            hideMessages();
            codeInput.value = '';
            renderCodeSlots();
            setHidden(codeForm, true);
            setHidden(phoneForm, false);
            phoneInput.focus();
        }

        function hideMessages() {
            setHidden(errorBox, true);
            setHidden(rateLimitedBox, true);
            setHidden(resendCooldownBox, true);
            setHidden(unavailableBox, true);
            setHidden(successBox, true);
            setHidden(providerWaivedBox, true);
        }

        function showError(messageKey) {
            if (errorText) {
                errorText.textContent = messages[messageKey] || messages.generic || errorText.textContent;
            }

            hideMessages();
            setHidden(errorBox, false);
            focusVerificationError(messageKey, phoneInput, codeInput);
        }

        function showRateLimited() {
            hideMessages();
            setHidden(rateLimitedBox, false);
        }

        function showResendCooldown(seconds) {
            hideMessages();
            setHidden(resendCooldownBox, false);
            startResendCooldown(seconds);
        }

        // Same-origin diagnostics remain reachable when content blockers disable Sentry.
        function reportVerifierFailure(error, sending) {
            const values = verifierFailureReport(error, sending);
            if (!values || !state.nonce) return;

            values.nonce = state.nonce;

            const generation = state.sessionGeneration;

            try {
                post('/phone-verification/verifier-failure', values).then(async function (response) {
                    // The report is also how the page learns what to do next:
                    // a second provider that can reach this number, or a gate
                    // the server already cleared.
                    if (!response || !response.ok) return;
                    if (state.sessionGeneration !== generation) return;
                    const body = await responseJson(response);
                    if (!body) return;
                    if (body.fallback_available) {
                        offerFallback();
                        return;
                    }
                    if (body.waived) completeWithProviderWaiver();
                }).catch(function () {
                    // Reporting is best-effort and never surfaces to the user.
                });
            } catch (e) {
                // Same.
            }
        }

        function offerFallback() {
            if (!fallbackBox || !state.nonce || !state.phone) return;

            setHidden(fallbackBox, false);
            window.LucideIcons?.initialize(fallbackBox);
        }

        function setFallbackBusy(busy) {
            fallbackButtons.forEach(function (button) {
                button.disabled = busy;
            });
            setHidden(document.getElementById(fallbackSpinnerId), !busy);
        }

        async function sendFallback(channel) {
            if (state.sending || state.confirming || !state.nonce || !state.phone) return;

            state.sending = true;
            setFallbackBusy(true);
            hideMessages();

            try {
                const response = await post('/phone-verification/fallback', {
                    nonce: state.nonce,
                    phone: state.phone,
                    channel: channel,
                });
                const data = await responseJson(response);

                if (!response.ok) {
                    const failure = apiFailure(response, data, 'session_failed', RESEND_COOLDOWN_SECONDS);
                    showFailure(codedError(failure.code, failure.retryAfter), true);
                    return;
                }

                if (data.waived) {
                    completeWithProviderWaiver();
                    return;
                }

                if (data.sent !== true) {
                    showUnavailable();
                    return;
                }

                state.fallbackChannel = channel;
                setHidden(fallbackBox, true);
                codeInput.value = '';
                showCodeForm();
            } catch (e) {
                showFailure(e, true);
                if (window.console) {
                    window.console.warn('verify-phone fallback:', e);
                }
            } finally {
                state.sending = false;
                setFallbackBusy(false);
            }
        }

        // Each failure the script can tell apart says what actually went wrong,
        // because "try again" is useless advice for a bad number or a stale code.
        function showFailure(error, sending) {
            const code = error && error.code;

            reportVerifierFailure(error, sending);

            // A send refused for being too soon is not a failure the user has to
            // do anything about, so it restarts the countdown they raced instead
            // of showing lockout copy. The button re-disables and the label ticks
            // down, which is the same feedback a successful send gives.
            if (code === RESEND_COOLDOWN) {
                showResendCooldown(error && error.retryAfter);
                return;
            }

            if (code === RATE_LIMITED || code === FIREBASE_RATE_LIMITED) {
                showRateLimited();
                return;
            }

            if (code === FIREBASE_QUOTA_EXCEEDED || code === UNCONFIGURED) {
                showUnavailable();
                return;
            }

            // A library that never loaded and a token the server refused are the
            // same story to the user: something on their side blocked the check.
            if (code === BLOCKED) {
                showError('security');
                return;
            }

            // The undeliverable copy tells the user to try another number,
            // which is wrong advice once a code has reached the one they gave.
            if (!sending && FIREBASE_MESSAGE_KEYS[code] === 'undeliverable') {
                showError('generic');
                return;
            }

            showError(FIREBASE_MESSAGE_KEYS[code]);
        }

        function showUnavailable() {
            hideMessages();
            setHidden(unavailableBox, false);
        }

        function showSuccess() {
            hideMessages();
            setHidden(successBox, false);
        }

        function showProviderWaived() {
            hideMessages();
            setHidden(providerWaivedBox, false);
        }

        function completeVerification() {
            state.firebaseIdToken = '';
            state.firebaseUser = null;
            state.confirmationResult = null;
            state.nonce = '';
            state.fallbackChannel = '';
            setHidden(fallbackBox, true);
            cooldown.stop();
            // The redirect takes a moment. Hide the form so the notice stands alone.
            setHidden(codeForm, true);
            showSuccess();
            window.location.assign(redirectUrl || ACCOUNT_URL);
        }

        // The gate is cleared, so the user goes where a verified user goes. The
        // send failed, so the page can be on either form when this runs.
        function completeWithProviderWaiver() {
            state.firebaseIdToken = '';
            state.firebaseUser = null;
            state.confirmationResult = null;
            state.nonce = '';
            state.fallbackChannel = '';
            cooldown.stop();
            setHidden(fallbackBox, true);
            setHidden(phoneForm, true);
            setHidden(codeForm, true);
            showProviderWaived();
            window.location.assign(redirectUrl || ACCOUNT_URL);
        }

        // Each box mirrors one character of the single underlying input. The
        // strings are written out in full because Tailwind's JIT scans this file
        // for literals, so a class assembled from fragments would never be
        // generated.
        const CODE_SLOT_BASE = 'flex h-14 flex-1 items-center justify-center rounded-xl border-2 text-xl font-semibold transition-colors duration-200 ';
        const CODE_SLOT_EMPTY = 'border-gray-200 bg-white text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100';
        const CODE_SLOT_FILLED = 'border-sky-500 bg-white text-gray-900 dark:border-sky-400 dark:bg-gray-800 dark:text-gray-100';
        const CODE_SLOT_ACTIVE = 'border-sky-500 bg-sky-50 text-gray-900 ring-2 ring-sky-500/20 dark:border-sky-400 dark:bg-sky-950/30 dark:text-gray-100';

        function renderCodeSlots() {
            if (!codeSlots.length) return;

            const value = digitsOnly(codeInput.value).slice(0, CODE_LENGTH);
            const focused = document.activeElement === codeInput;
            // The box the next character lands in, so an incomplete code shows
            // where the user is.
            const caret = value.length;

            codeSlots.forEach(function (slot, index) {
                slot.textContent = value.charAt(index);

                let state = CODE_SLOT_EMPTY;
                if (index < value.length) {
                    state = CODE_SLOT_FILLED;
                }
                if (focused && index === caret) {
                    state = CODE_SLOT_ACTIVE;
                }

                slot.className = CODE_SLOT_BASE + state;
            });
        }

        // Typing always appends, so the caret is pinned to the end. Without this
        // a tap in the middle of the row would drop the next digit into the
        // middle of the code.
        function pinCodeCaret() {
            const end = codeInput.value.length;
            try {
                codeInput.setSelectionRange(end, end);
            } catch (e) {
                // Not supported on this input type in some browsers.
            }
        }

        // Emptied after a rejected code so the boxes are ready for a fresh
        // attempt. Leaving the failed digits in place would also leave the
        // field complete, so the auto-submit could never fire again.
        function resetCodeInput() {
            codeInput.value = '';
            renderCodeSlots();
            codeInput.focus();
        }

        function showCodeForm() {
            setHidden(phoneForm, true);
            setHidden(codeForm, false);
            renderCodeSlots();
            codeInput.focus();
        }

        // The Firebase SDK hardcodes www.google.com for its reCAPTCHA script,
        // which is unreachable from mainland China and from networks that block
        // Google. It does reuse an already-present window.grecaptcha that exposes
        // render(), so loading Google's own recaptcha.net mirror first gives those
        // users a working verifier. Same keys, same tokens.
        function loadRecaptchaLibrary() {
            if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
                return Promise.resolve(true);
            }

            return new Promise(function (resolve) {
                let settled = false;

                function settle(loaded) {
                    if (settled) return;
                    settled = true;
                    window.clearTimeout(timer);
                    delete window[RECAPTCHA_READY_CALLBACK];
                    // A false result is not fatal: the SDK falls back to loading
                    // its own script from www.google.com.
                    resolve(loaded && !!(window.grecaptcha && window.grecaptcha.render));
                }

                const timer = window.setTimeout(function () {
                    settle(false);
                }, RECAPTCHA_LOAD_TIMEOUT_MS);

                // Defined before the script is appended, so a cached script that
                // executes immediately still finds its callback.
                window[RECAPTCHA_READY_CALLBACK] = function () {
                    settle(true);
                };

                const script = document.createElement('script');
                script.src = RECAPTCHA_MIRROR_SRC;
                script.async = true;
                script.defer = true;
                script.onerror = function () {
                    settle(false);
                };

                document.head.appendChild(script);
            });
        }

        async function ensureFirebase() {
            const firebase = window.firebase;
            if (!firebase || !firebase.auth) {
                throw codedError(BLOCKED);
            }

            await loadRecaptchaLibrary();

            if (!firebase.apps || firebase.apps.length === 0) {
                firebase.initializeApp(config);
            }

            if (!firebaseAppCheckSiteKey) {
                throw codedError(UNCONFIGURED);
            }
            if (!firebase.appCheck) {
                throw codedError(BLOCKED);
            }
            if (!state.appCheckInitialized) {
                firebase.appCheck().activate(
                    new firebase.appCheck.ReCaptchaEnterpriseProvider(firebaseAppCheckSiteKey),
                    true,
                );
                state.appCheckInitialized = true;
            }

            const auth = firebase.auth();
            await auth.setPersistence(firebase.auth.Auth.Persistence.SESSION);
            state.auth = auth;

            if (!state.recaptchaVerifier) {
                try {
                    state.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
                        'recaptcha-container',
                        { size: 'invisible' }
                    );
                    state.recaptchaWidgetId = await state.recaptchaVerifier.render();
                } catch (e) {
                    // Rendering fails when the reCAPTCHA library never arrived, so
                    // the user is told what to change rather than to retry.
                    state.recaptchaVerifier = null;
                    throw codedError(BLOCKED);
                }
            }

            return auth;
        }

        function resetRecaptcha() {
            if (
                state.recaptchaWidgetId !== null
                && window.grecaptcha
                && typeof window.grecaptcha.reset === 'function'
            ) {
                window.grecaptcha.reset(state.recaptchaWidgetId);
            }
        }

        async function requestSession(phone) {
            const response = await post('/phone-verification/session', {
                phone_e164: phone,
            });
            const data = await responseJson(response);

            if (!response.ok) {
                // A moderator waive, or a confirm on another device, resolves the
                // gate while this page waits. The session call then refuses with
                // 409 invalid_state, which means "nothing left to do", not an error.
                if (response.status === HTTP_CONFLICT && data && data.error && data.error.code === INVALID_STATE) {
                    completeVerification();
                    return null;
                }

                const failure = apiFailure(response, data, 'session_failed', RESEND_COOLDOWN_SECONDS);
                throw codedError(failure.code, failure.retryAfter);
            }

            if (data.status === 'waived') {
                completeVerification();
                return null;
            }

            if (data.sendable !== true) {
                showUnavailable();
                return null;
            }

            if (!data.nonce) {
                throw codedError('nonce_missing');
            }

            return data;
        }

        async function sendCode() {
            if (state.sending || state.confirming) return;

            const phone = hasCountryPicker ? composeE164() : phoneInput.value.trim();
            hideMessages();

            if (!PHONE_PATTERN.test(phone)) {
                showError('number');
                return;
            }

            state.sending = true;
            setBusy(sendButton, sendSpinnerId, true);
            cooldown.setSendBusy(true);
            setResendBusy(true);
            if (confirmButton) confirmButton.disabled = true;
            if (changeNumberButton) changeNumberButton.disabled = true;

            try {
                // Client readiness must be established before the backend spends
                // the user's daily session allowance or starts the SMS cooldown.
                const auth = await ensureFirebase();
                const session = await requestSession(phone);
                if (!session) return;

                state.sessionGeneration += 1;
                state.nonce = String(session.nonce);
                state.phone = phone;
                state.fallbackChannel = '';
                setHidden(fallbackBox, true);
                state.firebaseIdToken = '';
                state.firebaseUser = null;
                state.confirmationResult = await auth.signInWithPhoneNumber(phone, state.recaptchaVerifier);
                showCodeForm();
                startResendCooldown();
            } catch (e) {
                showFailure(e, true);
                if (window.console) {
                    window.console.warn('verify-phone:', e);
                }
            } finally {
                // A reCAPTCHA token is single-use, and the verifier is built once
                // and reused, so the widget is reset after every attempt. Resetting
                // only on failure leaves a resend replaying a spent token, which
                // Firebase rejects as an invalid app credential.
                resetRecaptcha();
                state.sending = false;
                setBusy(sendButton, sendSpinnerId, false);
                cooldown.setSendBusy(false);
                setResendBusy(false);
                if (confirmButton) confirmButton.disabled = false;
                if (changeNumberButton) changeNumberButton.disabled = false;
            }
        }

        async function confirmCode() {
            if (state.confirming || state.sending) return;

            const code = codeInput.value.replace(/\s+/g, '');
            hideMessages();

            const viaFallback = Boolean(state.fallbackChannel);
            const firebaseReady = state.firebaseIdToken
                || (CODE_PATTERN.test(code) && state.confirmationResult);

            if (!state.nonce || (viaFallback ? !CODE_PATTERN.test(code) : !firebaseReady)) {
                showError('code');
                return;
            }

            state.confirming = true;
            setBusy(confirmButton, confirmSpinnerId, true);
            cooldown.setSendBusy(true);
            // Disable without the spinner. Only a send animates the resend icon.
            cooldown.setResendBusy(true);
            if (changeNumberButton) changeNumberButton.disabled = true;

            try {
                if (viaFallback) {
                    await confirmFallbackCode(code);
                    return;
                }

                const result = await confirmWithCachedToken({
                    code,
                    postConfirmation: (firebaseIdToken, nonce) => post('/phone-verification/confirm', {
                        firebase_id_token: firebaseIdToken,
                        nonce,
                    }),
                    responseJson,
                    state,
                    warn: (error) => {
                        if (window.console) window.console.warn('verify-phone signout:', error);
                    },
                });
                if (result.stale) return;
                if (!result.verified) {
                    if (result.terminal) {
                        resetCodeInput();
                    }
                    const failure = apiFailure(
                        result.response,
                        result.data,
                        'confirm_failed',
                        RESEND_COOLDOWN_SECONDS,
                    );
                    showFailure(codedError(failure.code, failure.retryAfter), false);
                    return;
                }

                completeVerification();
            } catch (e) {
                showFailure(e, false);
                if (!state.firebaseIdToken) {
                    resetCodeInput();
                }
                if (window.console) {
                    window.console.warn('verify-phone:', e);
                }
            } finally {
                state.confirming = false;
                setBusy(confirmButton, confirmSpinnerId, false);
                cooldown.setSendBusy(false);
                cooldown.setResendBusy(false);
                if (changeNumberButton) changeNumberButton.disabled = false;
            }
        }

        // The provider that delivered the code is the one that checks it, so a
        // code from the fallback route never reaches the Firebase confirm.
        async function confirmFallbackCode(code) {
            const response = await post('/phone-verification/fallback/confirm', {
                nonce: state.nonce,
                code: code,
            });
            const data = await responseJson(response);

            if (!response.ok) {
                resetCodeInput();
                const failure = apiFailure(response, data, 'confirm_failed', RESEND_COOLDOWN_SECONDS);
                showFailure(codedError(failure.code, failure.retryAfter), false);
                return;
            }

            completeVerification();
        }

        fallbackButtons.forEach(function (button) {
            button.addEventListener('click', function () {
                sendFallback(button.getAttribute('data-channel'));
            });
        });

        phoneForm.addEventListener('submit', function (event) {
            event.preventDefault();
            sendCode();
        });

        codeForm.addEventListener('submit', function (event) {
            event.preventDefault();
            confirmCode();
        });

        if (codeSlots.length) {
            // Paste lands here too, since a pasted code fires `input` on the
            // single underlying field. Non-digits are dropped, so a code copied
            // as "123 456" or "code: 123456" still resolves.
            codeInput.addEventListener('input', function () {
                const digits = digitsOnly(codeInput.value).slice(0, CODE_LENGTH);
                if (codeInput.value !== digits) {
                    codeInput.value = digits;
                }

                renderCodeSlots();

                // A complete code submits itself. Retyping the last digit is the
                // whole remaining step, so making the user reach for the button
                // is friction with no purpose.
                if (digits.length === CODE_LENGTH && !state.confirming) {
                    confirmCode();
                }
            });

            codeInput.addEventListener('focus', function () {
                pinCodeCaret();
                renderCodeSlots();
            });

            codeInput.addEventListener('blur', renderCodeSlots);

            // Clicking a box focuses the field rather than moving the caret into
            // the middle of the value.
            codeInput.addEventListener('click', pinCodeCaret);

            codeInput.addEventListener('keyup', renderCodeSlots);
        }

        if (resendButton) {
            resendButton.addEventListener('click', function () {
                codeInput.value = '';
                renderCodeSlots();
                sendCode();
            });
        }

        if (changeNumberButton) {
            changeNumberButton.addEventListener('click', function () {
                changeNumber();
            });
        }

        if (hasCountryPicker) {
            // The server resolves the country from the request IP. Fall back to the
            // built-in default when it has none, or names one with no dial code.
            const geoIso = (root.getAttribute('data-default-country') || '').toLowerCase();
            applyCountry(countryByIso(geoIso) || countryByIso(DEFAULT_COUNTRY_ISO));

            createCountryListbox({
                button: countryButton,
                countries,
                createOption: countryOption,
                documentTarget: document,
                empty: countryEmpty,
                getSelectedIso: function () {
                    return state.country ? state.country.iso : '';
                },
                list: countryList,
                onSelect: applyCountry,
                panel: countryPanel,
                phoneInput,
                search: countrySearch,
                setHidden,
                wrapper: countryWrapper,
            });

            phoneInput.addEventListener('input', function (event) {
                absorbInternationalNumber(event.inputType === 'insertFromPaste');
            });

            countryDial.addEventListener('input', function () {
                const digits = digitsOnly(countryDial.value);
                // Keep the field showing a '+' prefix without fighting the caret on
                // every keystroke: only rewrite when the user dropped it.
                if (countryDial.value !== '+' + digits) {
                    countryDial.value = '+' + digits;
                }

                resizeDialInput();
                syncCountryFromDialInput();
            });

            // A dial code typed to completion moves on to the number, the same way
            // picking one from the list does.
            countryDial.addEventListener('keydown', function (event) {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    phoneInput.focus();
                }
            });

        }
    });
})();
